import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, staticUrl } from "@/lib/api";
import { EncounterCard } from "@/components/EncounterCard";
import { CommentsSection } from "@/components/social/CommentsSection";
import { Loader2, FileText, ShieldCheck, AlertTriangle, Brain, MapPin, Tag, ChevronLeft, Eye, Image as ImageIcon } from "lucide-react";

export default function SpeciesDetail() {
    const { slug } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get(`/species/${slug}`).then((r) => setData(r.data)).finally(() => setLoading(false));
    }, [slug]);

    if (loading) return <div className="ok-container py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
    if (!data) return <div className="ok-container py-20 text-center">Species not found.</div>;

    const s = data.species;
    return (
        <div className="ok-container py-8 lg:py-12 max-w-6xl mx-auto" data-testid="species-detail-page">
            <Link to="/species" className="font-mono text-[10px] uppercase tracking-[0.3em] opacity-60 hover:opacity-100 flex items-center gap-1 mb-6">
                <ChevronLeft className="w-3 h-3" /> Species Archive
            </Link>

            {/* HEADER */}
            <header className="grid lg:grid-cols-12 gap-8 mb-12">
                <div className="lg:col-span-5">
                    <div className="ok-card overflow-hidden relative" style={{ background: "var(--ok-image-frame)" }}>
                        {s.image_url ? (
                            <img src={staticUrl(s.image_url)} alt={s.name} className="w-full aspect-square object-cover" data-testid="species-image" />
                        ) : (
                            <div className="aspect-square flex flex-col items-center justify-center gap-3">
                                <ImageIcon className="w-12 h-12 text-[var(--ok-accent)] opacity-30" />
                                <div className="font-mono text-[10px] uppercase tracking-[0.3em] opacity-60">Dossier image pending</div>
                            </div>
                        )}
                        {s.designation && (
                            <div className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-[0.3em] px-2 py-1" style={{ background: "var(--ok-chip-overlay-bg)", color: "var(--ok-chip-overlay-color)", border: "1px solid var(--ok-chip-overlay-border)" }}>
                                {s.designation}
                            </div>
                        )}
                    </div>
                </div>
                <div className="lg:col-span-7 flex flex-col justify-center">
                    <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-[var(--ok-accent)] flex flex-wrap items-center gap-3 mb-3">
                        <FileText className="w-3 h-3" />
                        <span>Species dossier</span>
                        {s.classification && <><span className="opacity-40">·</span><span>{s.classification}</span></>}
                    </div>
                    <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-3" data-testid="species-name">{s.name}</h1>
                    {s.summary && <p className="opacity-85 text-sm sm:text-base leading-relaxed mb-5">{s.summary}</p>}

                    {/* Quick assessment row */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <Stat icon={<ShieldCheck className="w-3 h-3" />} k="Threat" v={s.threat_level || "—"} accent={s.threat_level === "High" ? "warn" : (s.threat_level === "0" || s.threat_level === "Low" ? "ok" : null)} />
                        <Stat icon={<Brain className="w-3 h-3" />} k="Intelligence" v={s.intelligence_assessment || "—"} />
                        <Stat icon={<Tag className="w-3 h-3" />} k="Disposition" v={s.disposition || "—"} />
                        <Stat icon={<MapPin className="w-3 h-3" />} k="Discovered" v={s.discovery_date || s.first_documented || "—"} />
                    </div>
                </div>
            </header>

            <div className="grid lg:grid-cols-12 gap-10">
                <main className="lg:col-span-8 space-y-12">
                    {/* CLASSIFICATION PANELS */}
                    <Section eyebrow="Section 01" title="Classification" testid="section-classification">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <Row k="Classification" v={s.classification} />
                            <Row k="Designation" v={s.designation} />
                            <Row k="Chemical basis" v={s.chemical_basis} />
                            <Row k="Structure" v={s.structure} />
                            <Row k="Discovery date" v={s.discovery_date} />
                            <Row k="Threat level" v={s.threat_level} accent={s.threat_level === "High" ? "warn" : null} />
                            <Row k="Disposition" v={s.disposition} />
                            <Row k="Intelligence" v={s.intelligence_assessment} accent="ok" />
                            <Row k="Location" v={s.location} />
                            <Row k="Origin (claimed)" v={s.origin_claimed} />
                            <Row k="First documented" v={s.first_documented} />
                            <Row k="Frequency" v={s.frequency} />
                        </div>
                    </Section>

                    {/* PHYSICAL CHARACTERISTICS */}
                    <Section eyebrow="Section 02" title="Physical Characteristics" testid="section-physical">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <Row k="Height" v={s.height} />
                            <Row k="Skin colour" v={s.skin_colour} />
                            <Row k="Skin texture" v={s.skin_texture} />
                            <Row k="Eyes" v={s.eye_shape} />
                            <Row k="Head" v={s.head_shape} />
                            <Row k="Mouth" v={s.mouth_type} />
                            <Row k="Limbs" v={s.limb_count} />
                            <Row k="Communication" v={s.communication} />
                            <Row k="Movement" v={s.movement} />
                        </div>
                    </Section>

                    {/* BEHAVIOUR NOTES */}
                    {s.behaviour_notes && (
                        <Section eyebrow="Section 03" title="Behaviour Notes" testid="section-behaviour">
                            <p className="text-sm sm:text-base opacity-90 leading-relaxed">{s.behaviour_notes}</p>
                            {s.slug === "shadow-being" && (
                                <div className="mt-4 ok-card p-4 flex items-start gap-3" style={{ background: "var(--ok-surface)" }}>
                                    <AlertTriangle className="w-4 h-4 text-[#e6c47a] flex-shrink-0 mt-0.5" />
                                    <p className="text-xs opacity-80 leading-relaxed">
                                        Characteristics above are <strong>witness-reported</strong> and remain unverified.
                                        This dossier records consistent reporting, not proven fact.
                                    </p>
                                </div>
                            )}
                        </Section>
                    )}

                    {/* COMMON HUMAN DESIGNATIONS */}
                    {(s.common_designations || []).length > 0 && (
                        <Section eyebrow="Section 04" title="Common Human Designations" testid="section-designations">
                            <div className="flex flex-wrap gap-2">
                                {s.common_designations.map((d) => (
                                    <span key={d} className="ok-tag" style={{ color: "var(--ok-accent)", borderColor: "var(--ok-accent)" }}>{d}</span>
                                ))}
                            </div>
                        </Section>
                    )}

                    {/* ANATOMICAL STUDIES */}
                    {(s.anatomical_studies || []).length > 0 && (
                        <Section eyebrow="Section 05" title="Anatomical & Profile Studies" testid="section-anatomy">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {s.anatomical_studies.map((a, i) => (
                                    <div key={i} className="ok-card p-3 flex items-start gap-2.5">
                                        <Eye className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${a.available ? "text-[var(--ok-accent)]" : "opacity-30"}`} />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm">{a.label}</div>
                                            <div className="font-mono text-[9px] uppercase tracking-[0.3em] opacity-50 mt-0.5">
                                                {a.available ? "On file" : "Pending dossier upload"}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Section>
                    )}

                    {/* RELATED ENCOUNTERS */}
                    <Section eyebrow="Section 06" title="Witness Correlations" testid="species-related-encounters">
                        {data.encounters.length === 0 ? (
                            <div className="text-sm opacity-60 ok-card p-6">No reported encounters yet for this entity.</div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {data.encounters.map((e) => <EncounterCard key={e.id} e={e} />)}
                            </div>
                        )}
                    </Section>
                </main>

                {/* SIDEBAR */}
                <aside className="lg:col-span-4 lg:sticky lg:top-24 self-start">
                    <div className="ok-card p-5 space-y-4" style={{ background: "var(--ok-glass-bg-strong)", borderColor: "var(--ok-glass-border)" }} data-testid="species-sidebar">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ok-accent)] flex items-center gap-2">
                            <FileText className="w-3 h-3" /> Archive metadata
                        </div>
                        <dl className="space-y-2.5 text-xs">
                            <SidebarRow k="Slug" v={s.slug} />
                            <SidebarRow k="Designation" v={s.designation} />
                            <SidebarRow k="Classification" v={s.classification} />
                            <SidebarRow k="Threat level" v={s.threat_level} accent={s.threat_level === "High" ? "warn" : (s.threat_level === "0" || s.threat_level === "Low" ? "ok" : null)} />
                            <SidebarRow k="Intelligence" v={s.intelligence_assessment} />
                            <SidebarRow k="Frequency" v={s.frequency} />
                            <SidebarRow k="Witnesses on file" v={String(data.encounters.length)} />
                        </dl>
                    </div>
                </aside>
            </div>
            <CommentsSection targetType="species" targetId={s.slug} />
        </div>
    );
}

function Section({ eyebrow, title, children, testid }) {
    return (
        <section data-testid={testid}>
            <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-[var(--ok-accent)] mb-2">{eyebrow}</div>
            <h2 className="font-display text-2xl sm:text-3xl mb-5 tracking-tight">{title}</h2>
            {children}
        </section>
    );
}

function Row({ k, v, accent }) {
    if (!v) return null;
    return (
        <div className="ok-card p-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] opacity-60">{k}</div>
            <div className="text-sm mt-1" style={{ color: accent === "warn" ? "#e6c47a" : accent === "ok" ? "var(--ok-accent)" : "inherit" }}>{v}</div>
        </div>
    );
}

function SidebarRow({ k, v, accent }) {
    if (!v) return null;
    return (
        <div className="flex items-start justify-between gap-3">
            <dt className="font-mono text-[10px] uppercase tracking-[0.3em] opacity-60 flex-shrink-0">{k}</dt>
            <dd className="font-display font-bold text-xs text-right break-words" style={{ color: accent === "warn" ? "#e6c47a" : accent === "ok" ? "var(--ok-accent)" : "var(--ok-on-glass)" }}>{v}</dd>
        </div>
    );
}

function Stat({ icon, k, v, accent }) {
    return (
        <div className="ok-card p-3">
            <div className="font-mono text-[9px] uppercase tracking-[0.3em] opacity-60 flex items-center gap-1.5">{icon}{k}</div>
            <div className="text-sm font-display font-bold mt-1 truncate" style={{ color: accent === "warn" ? "#e6c47a" : accent === "ok" ? "var(--ok-accent)" : "inherit" }}>{v}</div>
        </div>
    );
}
